import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Event as NavigationEvent,
  NavigationStart,
  Router,
} from '@angular/router';
import { AdobeTargetService } from '@upr-web-primer/adobe-target';
import { BrowserDetectorService } from '@upr-web-primer/browser-detector';
import {
  AppDataLayerService,
  DigitalDataService,
} from '@upr-web-primer/digital-data';
import { GdsFooterService } from '@upr-web-primer/gds-footer';
import { GlobalNavService } from '@upr-web-primer/gds-global-nav';
// primer imports
import { NavigationService } from '@upr-web-primer/navigation';
import { NotificationService } from '@upr-web-primer/notification';
import { PageLabelsResolver } from '@upr-web-primer/page-labels';
import { TcmPictureService } from '@upr-web-primer/tcm-picture';
import { get, isEmpty } from '@upr-web-primer/udx-utils';
import { ViewportService } from '@upr-web-primer/viewport';
import { WINDOW } from '@upr-web-primer/window';
import * as moment from 'moment-timezone';
import { CookieService } from 'ngx-cookie-service';
// rxjs

import { LocalStorageService } from '@upr-web-primer/storage';
import {
  UdxSpinnerModule,
  UdxSpinnerService,
} from '@upr-web-primer/udx-spinner';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { appDefault } from '../config/app-default.constant';
import { environment } from '../environments/environment';
import { ApplicationDataService } from './services/application-data.service';
import { CommonUtilityService } from './services/common-utility.service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  providers: [UdxSpinnerModule],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  stickyNavSub: Subscription;
  isStickyNavOpen: boolean;
  viewportSub: Subscription;
  spinnerSub: Subscription;
  notificationSub: Subscription;
  adobeTargetDisabled: boolean;
  globalNavigation: boolean;
  pageLabels: any = null;
  subscriptions: Subscription;
  parkHours: any[] = [];
  language: string;
  country: string;
  skipToMainContentLabel: string;
  defaultLanguage: string;
  gtmUrl: string;
  digitalDataObj: any;
  digitalDataLayerObj: any;
  navRegularData: string;
  footerRegularData: string;

  quantity: any = 0;
  cartItemsLoaded = false;
  basicVersion: boolean | undefined;
  dashboardVersion: any;
  isProd: boolean;
  eventData: any;
  errorData: any;
  showNav = true;
  showFooter = true;
  favIconUrl = environment.faviconUrl;
  selfServiceEnabled = environment?.selfServiceEnabled;
  cfarEnabled = environment?.cfarEnabled;
  constructor(
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    @Inject(WINDOW) private readonly window: any,
    @Inject('AppDefaults') private readonly appDefaults: any,
    private readonly spinner: UdxSpinnerService,
    private readonly layoutNavigation: NavigationService,
    private readonly viewport: ViewportService,
    private readonly adobeTarget: AdobeTargetService,
    private readonly zone: NgZone,
    private readonly browserDetection: BrowserDetectorService,
    private readonly renderer2: Renderer2,
    private readonly pictureService: TcmPictureService,
    private readonly common: CommonUtilityService,
    private readonly pageLabelsService: PageLabelsResolver,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly digitalDataService: DigitalDataService,
    private readonly appDataLayerService: AppDataLayerService,
    private readonly applicationDataService: ApplicationDataService,
    private readonly titleService: Title,
    private readonly metaService: Meta,
    private readonly globalNav: GlobalNavService,
    private readonly globalFooter: GdsFooterService,
    private readonly cookieService: CookieService,
    private readonly localStorage: LocalStorageService,
    private readonly notification: NotificationService,
    @Inject('GlobalDefaults') public globalDefault: any,

    @Inject('AppRoutes') public appRoutes: any, // private notification: NotificationService, // private cdRef: ChangeDetectorRef,
  ) {
    this.adobeTargetDisabled = get(appDefaults, 'ADOBE_TARGET_DISABLED', false);

    this.language = this.defaultLanguage;

    this.globalNavigation = get(
      this.appDefaults,
      'GLOBAL_NAVIGATION_ENABLED',
      false,
    );

    // Set Default TimeZone
    if (this.appDefaults.UPR_TIME_ZONE) {
      moment.tz.setDefault(this.appDefaults.UPR_TIME_ZONE);
    }
    this.applicationDataService.readCookieData();

    // Subscribe to nav/footer data in constructor to avoid ExpressionChangedAfterItHasBeenCheckedError
    // These observables may emit synchronously; subscribing here ensures values are stable before first CD
    this.globalNav.dataReadyNotification$.subscribe((data: any) => {
      this.navRegularData = JSON.stringify(data);
      this.cdRef.markForCheck();
    });
    this.globalFooter.dataReadyNotification$.subscribe((data: any) => {
      this.footerRegularData = JSON.stringify(data);
      this.cdRef.markForCheck();
    });

    // OnPush requires an explicit tick whenever the global <upr-notification>
    // banner is shown/hidden via NotificationService — otherwise a show()
    // triggered outside the active CD cycle (e.g. from OtpService after the
    // DAL's cached ReplaySubject resolves) updates the banner state but the
    // host view never re-renders and the guest sees nothing.
    this.notificationSub = this.notification.notifications$.subscribe(() => {
      this.cdRef.markForCheck();
    });
  }

  ngOnInit(): void {
    // Initialize favicon from page labels
    this.initializeFavicon();

    this.common.getPageLabels().subscribe((data: any) => {
      this.pageLabels = data?.elements ?? null;
      this.cdRef.markForCheck();
    });
    if (this.window.location.href.search('signoff') > -1) {
      this.common.getLogout(this.pageLabels);
    }
    if (this.window?.location?.href.includes('purchaseHistory=true')) {
      sessionStorage.setItem('purchaseHistory', 'true');
    }
    if (window.location.href.includes(this.appRoutes.HOTELS_CHECK_IN)) {
      this.showNav = false;
      this.showFooter = false;
    }
    /**
     * Self-service order navigation handling:
     * When users navigate to an 'orders' URL with selfServiceEnabled:
     * - If orderId exists in localStorage: Route to specific order details page
     * - If orderId is missing (deep link, cleared storage, private browsing): Redirect to order history
     *
     * The orderId check prevents users from seeing a blank/error state when arriving
     * via deep links without prior session context. This ensures a graceful fallback
     * to the order history page where they can select their order.
     */
    if (
      this.selfServiceEnabled &&
      this.window?.location?.href.includes('orders')
    ) {
      const orderId = this.localStorage.getItem('orderId');
      if (orderId) {
        this.common.changeRoute(`${this.appRoutes.ORDER_DETAILS}/${orderId}`);
      } else {
        //  Log a warning for developers to indicate that the orderId was not found in localStorage & fallback not needed
        console.warn(
          'Order ID not found in localStorage. Redirecting to order history.',
        );
      }
    }

    if (window.location.href.includes(this.appRoutes.CART)) {
      this.cookieService.set(
        'isCart',
        JSON.stringify(true),
        0,
        '/',
        `${environment.cookieSubDomain}`,
      );
      this.common.onClickRedirectWTS('', '', true);
    }
    // this.globalFooter.init();
    this.isProd = environment.production ? environment.production : false;
    this.router.events.subscribe((event: any) => {
      if (event?.url) {
        const url = event.url;
        this.common.updatePageInfo(url);
      }
      const currentPath: any = event?.routerEvent?.url?.split('/').pop();
      if (currentPath) {
        if (environment.isRToasterTagEnabled) this.callTrackAndRecommend();
      }
    });
    if (!environment?.config?.dashboard?.isNavigate) {
      this.fetchProgressBar();
    }
    if (!this.common.IsIconArrayDataExist().length) {
      this.fetchProgressBar();
    }
    this.titleService.setTitle(environment?.seoTitle);
    this.metaService.updateTag({
      name: this.globalDefault?.seoDescription,
      content: environment?.seoDescription,
    });
    this.zone.runOutsideAngular(() => {
      this.stickyNavSub = this.layoutNavigation.stickyNavOpen$
        .pipe(distinctUntilChanged())
        .subscribe((isStickyNavOpen) => {
          this.zone.run(() => {
            this.isStickyNavOpen = isStickyNavOpen;
            this.cdRef.markForCheck();
          });
        });
    });
    if (environment.isRToasterTagEnabled) this.injectRToasterScript();
    this.router.events.subscribe((event: NavigationEvent) => {
      this.navigationInterceptor(event);
    });
    this.viewportSub = this.viewport.viewport$.subscribe((info) => {
      this.window.viewPortStatus = info;
      this.window.dispatchEvent(
        new CustomEvent('viewPortStatus', { detail: info }),
      );
    });
    this.spinnerSub = this.spinner.spinner$.subscribe((state: boolean) => {
      this.window.spinnerState = state;
      this.window.dispatchEvent(
        new CustomEvent('spinnerState', {
          detail: {
            spinner: state,
          },
        }),
      );
    });
    const browser = this.browserDetection.detect() || {};
    this.window.browserDetection = browser;
    if (!isEmpty(browser) && browser.name) {
      this.renderer2.addClass(this.window.document.body, browser.name);
      this.renderer2.addClass(
        this.window.document.body,
        browser.name + get(browser, 'version', '').split('.')[0],
      );
    }
    const mBox = get(this.appDefaults, 'ADOBE_MBOX');
    if (mBox) {
      this.adobeTarget.setMbox(mBox);
    }
    this.pictureService.isRenderingComplete$.next(true);
    this.injectScript();
    this.injectNoScriptAtBody();
    const windowObj: any = window;
    setTimeout(() => {
      this.digitalDataObj = windowObj['digitalData'] ?? {};
      this.digitalDataLayerObj = windowObj['dataLayer'] ?? [];
      const index = this.digitalDataLayerObj.findIndex((obj: any) =>
        Object.keys(obj).includes('page'),
      );
      if (index !== -1) {
        windowObj['dataLayer'][index] = this.digitalDataObj;
      }
    }, 3000);
  }

  private initializeFavicon(): void {
    this.pageLabelsService?.get().then((pageLabels: any) => {
      this.pageLabels = pageLabels ?? null;
      const favIconUrl = pageLabels?.['favIconUrl'];
      if (favIconUrl) {
        this.favIconUrl = this.common?.prependImageUrl(
          environment?.tridionBaseUrl + favIconUrl,
        );
      }
      this.updateFavicon(this.favIconUrl);
      this.cdRef.markForCheck();
    });
  }

  updateFavicon(faviconUrl: string): void {
    const link: HTMLLinkElement = document.createElement('link');
    link.rel = 'shortcut icon';
    link.href = faviconUrl;
    link.type = 'image/x-icon';
    document.getElementsByTagName('head')[0].appendChild(link);
  }

  /**
   * Initializes pageLabels if not already set.
   * This method is used primarily in tests but can be called to ensure pageLabels are loaded.
   */
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error TS6133: Method is used in tests via bracket notation (component['initializePageLabels']())
  private async initializePageLabels(): Promise<void> {
    this.pageLabels ??= (await this.pageLabelsService?.get()) ?? null;
  }

  private navigationInterceptor(event: NavigationEvent): void {
    if (event instanceof NavigationStart) {
      this.spinner.show();
      this.window.appRouteStatus = 'NavigationStart';
      this.common.initAnalytics();
    }
    if (event instanceof NavigationEnd) {
      this.spinner.hide();
      this.window.appRouteStatus = 'NavigationEnd';
      if (
        !this.common.isDDLEnabled() &&
        !this.adobeTargetDisabled &&
        (!get(event, 'id') || event.id > 1)
      ) {
        this.adobeTarget.getABTestOffer();
      }
      this.window.dispatchEvent(
        new CustomEvent('NavigationEnd', { detail: event }),
      );
      this.common.scrollTop();
    }
    if (event instanceof NavigationCancel) {
      this.spinner.hide();
      this.window.appRouteStatus = 'NavigationCancel';
    }
    if (event instanceof NavigationError) {
      this.spinner.hide();
      this.window.appRouteStatus = 'NavigationError';
    }
    this.window.appRouteStatusEvent = event;
  }

  injectScript(): void {
    const gtm = environment.gtmId;
    this.gtmUrl = `https://www.googletagmanager.com/gtm.js?id=${gtm}`;
    const script = this.renderer2.createElement('script');
    script.src = this.gtmUrl;
    this.renderer2.appendChild(this.document.head, script);
    this.appDataLayerService.digitalDataEnabled = true;
    this.initDigitalData();
    this.appDataLayerService.initDigitalDataLayer();
  }

  injectNoScriptAtBody(): void {
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    const gtm = environment.gtmId;
    this.gtmUrl = `https://www.googletagmanager.com/ns.html?id=${gtm}`;
    iframe.setAttribute('src', this.gtmUrl);
    iframe.setAttribute('height', '0');
    iframe.setAttribute('width', '0');
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.prepend(noscript);
  }

  private initDigitalData(): void {
    const digitalDataEnabled = get(appDefault, 'DIGITAL_DATA_ENABLED', true);
    if (digitalDataEnabled) {
      this.digitalDataService.init();
    }
  }

  loadGdsNav(): void {
    this.globalNav.init();
    this.globalFooter.init();
  }

  ngOnDestroy(): void {
    this.stickyNavSub.unsubscribe();
    this.viewportSub.unsubscribe();
    this.spinnerSub.unsubscribe();
    this.notificationSub?.unsubscribe();
  }

  fetchProgressBar(): void {
    this.common?.getProgressBarData()?.subscribe(
      (res: any) => {
        this.common.setIconArrayData(res?.[0]?.data?.progressBar);
      },
      (_error: any) => {
        this.errorData = _error;
      },
    );
  }

  injectRToasterScript(): void {
    const rToasterId = environment.rToasterTagId;
    this.loadScript(`//js.rtoaster.jp/${rToasterId}/rt.js`).then(() => {
      this.loadScript('//js.rtoaster.jp/Rtoaster.Popup.js').then(() => {});
    });
  }

  loadScript(src: any, async = true, charset = 'utf-8'): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const script = this.renderer2.createElement('script');
      script.type = 'text/javascript';
      script.async = async;
      script.charset = charset;
      script.src = src;
      script.onload = (): any => {
        resolve();
      };
      script.onerror = (error: any): any => {
        reject(error instanceof Error ? error : new Error(String(error)));
      };

      this.renderer2.appendChild(
        this.document.getElementsByTagName('head')[0],
        script,
      );
    });
  }

  callTrackAndRecommend(): void {
    if (this.window['Rtoaster']) {
      const RToasterTrackScript = this.document.createElement('script');
      RToasterTrackScript.type = 'text/javascript';
      RToasterTrackScript.text = 'Rtoaster.trackAndRecommend()';
      this.renderer2.appendChild(
        this.document.getElementsByTagName('head')[0],
        RToasterTrackScript,
      );
    }
  }

  addHistoryRoute(): void {
    setTimeout(() => {
      const element = this.document.getElementById('order_history');
      element?.focus();
    }, 500);
  }

  componentLoaded(event?: any): any {
    if (!this.cartItemsLoaded) {
      this.cartItemsLoaded = true;
      this.common.getCartItems();
    }
    if (event?.target?.data?.classList === 'gds-logo') {
      const clickedElement = event.target as HTMLElement;
      this.renderer2.listen(clickedElement, 'click', (_event) => {
        this.eventData = _event;
        window.location.href = 'https://www.usj.co.jp/web/ja/jp';
      });
    }
  }

  globalNavButtonElementClicked(event: any): any {
    if (environment?.basic_version == true) {
      this.dashboardVersion = this.globalDefault?.DASHBOARD_VERSION_ARRAY[1];
    } else {
      this.dashboardVersion = this.globalDefault?.DASHBOARD_VERSION_ARRAY[0];
    }
    if (
      event?.target?.data?.button?.svgOptions?.iconName ===
      this.globalDefault.CART
    ) {
      if (
        this.dashboardVersion === this.globalDefault.DASHBOARD_VERSION_ARRAY[1]
      ) {
        window.open(environment.getTicketsLink, '_self');
      } else {
        const button: any = event?.target?.data?.button;
        if (button && button?.description === 'cart') {
          this.router.navigateByUrl(this.appRoutes.CART);
        }
      }
    }
  }
}
